import { useState, type FormEvent, type ReactNode } from 'react';
import { Link, useParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  GROUP_MEMBER_ROLES,
  type GroupDetailResponse,
  type GroupMemberRole,
  type GroupSummaryResponse,
} from '@tabita/contracts';
import { formatDate } from '@tabita/i18n';
import { useAuth } from '@/auth/auth-context';
import { useI18n } from '@/i18n/i18n-provider';
import { groupsApi } from '@/services/groups.api';
import { peopleApi } from '@/services/people.api';
import { ApiError } from '@/services/api-client';
import { Alert, Button, EmptyState, Field, Spinner, StatusBadge } from '@/components/ui';
import { LocationMapPicker } from '@/components/location-map-picker';

/**
 * One group: its details and its roster.
 *
 * The roster is the reason this screen exists. Names come from People, resolved
 * server-side at read time, so a member who marries shows their current name
 * here without Groups storing or updating anything.
 */
export function GroupDetailPage() {
  const { id = '' } = useParams();
  const { t, locale } = useI18n();
  const { can } = useAuth();
  const queryClient = useQueryClient();

  const [notice, setNotice] = useState<string | null>(null);
  const [coordinates, setCoordinates] = useState<{ lat: number; lng: number } | null>(null);

  const query = useQuery({
    queryKey: ['groups', id],
    queryFn: ({ signal }) => groupsApi.get(id, signal),
  });

  const onChanged = async (group: GroupDetailResponse, message: string | null) => {
    queryClient.setQueryData(['groups', id], group);
    await queryClient.invalidateQueries({ queryKey: ['groups'], exact: false });
    setNotice(message);
  };

  const archive = useMutation({
    mutationFn: (reason: string) => groupsApi.archive(id, reason),
    onSuccess: (group) =>
      onChanged(group, t('groups.group.archived', { values: { name: group.name } })),
  });

  const restore = useMutation({
    mutationFn: () => groupsApi.restore(id),
    onSuccess: (group) =>
      onChanged(group, t('groups.group.restored', { values: { name: group.name } })),
  });

  const addMember = useMutation({
    mutationFn: (personId: string) => groupsApi.addMember(id, { personId, role: 'MEMBER' }),
    onSuccess: (group) => onChanged(group, null),
  });

  const changeRole = useMutation({
    mutationFn: (input: { personId: string; role: GroupMemberRole }) =>
      groupsApi.changeMemberRole(id, input.personId, input.role),
    onSuccess: (group) => onChanged(group, null),
  });

  const removeMember = useMutation({
    mutationFn: (personId: string) => groupsApi.removeMember(id, personId),
    onSuccess: (group) => onChanged(group, null),
  });

  const updateLocation = useMutation({
    mutationFn: (input: { latitude: number | null; longitude: number | null }) =>
      groupsApi.update(id, input),
    onSuccess: (group) => {
      void onChanged(group, t('groups.group.locationSaved'));
      setCoordinates(null);
    },
  });

  const updateParent = useMutation({
    mutationFn: (parentGroupId: string | null) => groupsApi.update(id, { parentGroupId }),
    onSuccess: (group) => onChanged(group, t('groups.group.parentGroupSaved')),
  });

  if (query.isPending) return <Spinner label={t('common.states.loading')} />;

  if (query.isError) {
    return (
      <div className="page">
        <Link to="/groups" className="back-link">
          {t('common.actions.back')}
        </Link>
        <Alert tone="danger">{describeError(query.error, t)}</Alert>
      </div>
    );
  }

  const group = query.data;
  const isArchived = group.archivedAt !== null;
  const isFull = group.capacity !== null && group.memberCount >= group.capacity;

  const mutationError = [
    addMember,
    changeRole,
    removeMember,
    archive,
    restore,
    updateLocation,
    updateParent,
  ].find((mutation) => mutation.isError);

  return (
    <div className="page">
      <Link to="/groups" className="back-link">
        {t('common.actions.back')}
      </Link>

      <header className="page-header">
        <div>
          <h1 className="page-title">{group.name}</h1>
          <StatusBadge status={group.status} label={t(`groups.status.${group.status}`)} />
        </div>

        <div className="page-actions">
          {can('communications.campaign.create') && !isArchived && (
            <Link to={`/communications/new?groupId=${group.id}`} className="btn">
              {t('groups.group.sendMessage')}
            </Link>
          )}
          {can('groups.group.archive') &&
            (isArchived ? (
              <Button
                variant="secondary"
                loading={restore.isPending}
                onClick={() => restore.mutate()}
              >
                {t('common.actions.restore')}
              </Button>
            ) : (
              <ArchiveButton group={group} onConfirm={(reason) => archive.mutate(reason)} />
            ))}
        </div>
      </header>

      {notice && <Alert tone="success">{notice}</Alert>}
      {mutationError && <Alert tone="danger">{describeError(mutationError.error, t)}</Alert>}
      {isArchived && <Alert tone="warning">{t('groups.group.archivedHint')}</Alert>}

      <dl className="detail-grid">
        <Detail label={t('groups.group.description')} value={group.description} />
        <Detail
          label={t('groups.group.meetingDay')}
          value={group.meetingDay ? t(`groups.day.${group.meetingDay}`) : null}
        />
        <Detail label={t('groups.group.meetingTime')} value={group.meetingTime} dir="ltr" />
        <Detail label={t('groups.group.meetingLocation')} value={group.meetingLocation} />
        <Detail
          label={t('groups.group.capacity')}
          value={group.capacity === null ? null : String(group.capacity)}
          dir="ltr"
        />
        <Detail
          label={t('groups.group.parentGroup')}
          value={
            group.parentGroupId && group.parentGroupName ? (
              <Link to={`/groups/${group.parentGroupId}`}>{group.parentGroupName}</Link>
            ) : null
          }
        />
        <Detail label={t('common.labels.createdAt')} value={formatDate(group.createdAt, locale)} />
      </dl>

      <ChildGroupsSection groupId={group.id} />

      {can('groups.group.update') && !isArchived && (
        <section className="settings-card">
          <h2 className="settings-card-title">{t('groups.group.location')}</h2>
          <LocationMapPicker
            latitude={coordinates?.lat ?? group.latitude}
            longitude={coordinates?.lng ?? group.longitude}
            onChange={(lat, lng) => setCoordinates({ lat, lng })}
          />
          {coordinates && (
            <Button
              variant="primary"
              loading={updateLocation.isPending}
              onClick={() =>
                updateLocation.mutate({ latitude: coordinates.lat, longitude: coordinates.lng })
              }
            >
              {t('common.actions.save')}
            </Button>
          )}
        </section>
      )}

      {can('groups.group.update') && !isArchived && (
        <section className="settings-card">
          <h2 className="settings-card-title">{t('groups.group.parentGroup')}</h2>
          <ParentGroupPicker
            excludeId={group.id}
            onPick={(parentGroupId) => updateParent.mutate(parentGroupId)}
          />
          {group.parentGroupId && (
            <Button
              variant="ghost"
              loading={updateParent.isPending}
              onClick={() => updateParent.mutate(null)}
            >
              {t('groups.group.removeParent')}
            </Button>
          )}
        </section>
      )}

      <section className="roster">
        <header className="page-header">
          <h2 className="page-title">
            {t('groups.group.members')} ·{' '}
            {t('groups.group.memberCount', { count: group.memberCount })}
          </h2>
          {can('groups.member.manage') && !isArchived && (
            <AddMemberPicker
              existing={group.members.map((member) => member.personId)}
              disabled={isFull}
              onPick={(personId) => addMember.mutate(personId)}
            />
          )}
        </header>

        {group.members.length === 0 ? (
          <EmptyState title={t('common.states.empty')} />
        ) : (
          <div className="table-scroll">
            <table className="table">
              <thead>
                <tr>
                  <th scope="col">{t('people.person.singular')}</th>
                  <th scope="col">{t('groups.group.role')}</th>
                  <th scope="col">{t('groups.group.joined')}</th>
                  <th scope="col" />
                </tr>
              </thead>
              <tbody>
                {group.members.map((member) => (
                  <tr key={member.personId}>
                    <td>
                      {/* The roster links back into People: a leader looking at
                          a member usually wants their contact details next. */}
                      <Link to={`/people/${member.personId}`} className="person-name">
                        {member.displayName}
                      </Link>
                    </td>
                    <td>
                      {can('groups.member.manage') && !isArchived ? (
                        <select
                          aria-label={t('groups.group.role')}
                          className="field-input"
                          value={member.role}
                          onChange={(event) =>
                            changeRole.mutate({
                              personId: member.personId,
                              role: event.target.value as GroupMemberRole,
                            })
                          }
                        >
                          {GROUP_MEMBER_ROLES.filter(
                            (role) =>
                              (role !== 'LEADER' && role !== 'CO_LEADER') ||
                              can('groups.member.assign') ||
                              // Keep the member's current role selectable even
                              // without the promotion permission — this select
                              // must still be able to demote them without
                              // silently dropping the option showing their
                              // current, already-assigned role.
                              role === member.role,
                          ).map((role) => (
                            <option key={role} value={role}>
                              {t(`groups.role.${role}`)}
                            </option>
                          ))}
                        </select>
                      ) : (
                        t(`groups.role.${member.role}`)
                      )}
                    </td>
                    <td>{formatDate(member.joinedAt, locale)}</td>
                    <td>
                      {can('groups.member.manage') && !isArchived && (
                        <Button
                          variant="ghost"
                          onClick={() => removeMember.mutate(member.personId)}
                          aria-label={`${t('groups.group.removeMember')}: ${member.displayName}`}
                        >
                          {t('groups.group.removeMember')}
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}

// --- Pieces ------------------------------------------------------------------

function Detail({ label, value, dir }: { label: string; value: ReactNode | null; dir?: 'ltr' }) {
  return (
    <div className="detail-row">
      <dt className="detail-label">{label}</dt>
      <dd className="detail-value" dir={dir}>
        {value ?? <span className="cell-empty">—</span>}
      </dd>
    </div>
  );
}

/**
 * Picks someone from the directory to add.
 *
 * A search box rather than a full dropdown of everyone: a church of two
 * thousand cannot be a `<select>`, and the person adding a member almost always
 * knows the name they are looking for.
 */
function AddMemberPicker({
  existing,
  disabled,
  onPick,
}: {
  existing: readonly string[];
  disabled: boolean;
  onPick: (personId: string) => void;
}) {
  const { t } = useI18n();
  const [search, setSearch] = useState('');

  const query = useQuery({
    queryKey: ['people', 'picker', search],
    queryFn: ({ signal }) => peopleApi.list({ search, limit: 8 }, signal),
    // Only once there is something to search for. An empty query would list the
    // whole directory to fill a picker nobody has typed into yet.
    enabled: search.trim().length >= 2,
  });

  const candidates = (query.data?.data ?? []).filter(
    (person) => !existing.includes(person.id) && !person.archivedAt,
  );

  return (
    <div className="member-picker">
      <Field
        label={t('groups.group.addMember')}
        name="member-search"
        type="search"
        value={search}
        onChange={(event) => setSearch(event.target.value)}
        placeholder={t('groups.group.choosePerson')}
        disabled={disabled}
      />

      {/* The full group says so rather than silently rejecting the click. */}
      {disabled && <p className="field-hint">{t('errors.GROUPS_CAPACITY_REACHED')}</p>}

      {!disabled && candidates.length > 0 && (
        <ul className="picker-results">
          {candidates.map((person) => (
            <li key={person.id}>
              <Button
                variant="ghost"
                onClick={() => {
                  onPick(person.id);
                  setSearch('');
                }}
              >
                {person.displayName}
              </Button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/**
 * The groups directly under this one on the org chart. Silent when there are
 * none — the common case is a leaf group, and an always-visible "Sub-groups:
 * none" row on every one of those would be noise, not information.
 */
function ChildGroupsSection({ groupId }: { groupId: string }) {
  const { t } = useI18n();

  const query = useQuery({
    queryKey: ['groups', groupId, 'children'],
    queryFn: ({ signal }) => groupsApi.list({ parentGroupId: groupId, limit: 100 }, signal),
  });

  const children = query.data?.data ?? [];
  if (children.length === 0) return null;

  return (
    <section className="settings-card">
      <h2 className="settings-card-title">{t('groups.group.subGroups')}</h2>
      <ul className="chip-list">
        {children.map((child) => (
          <li key={child.id}>
            <Link to={`/groups/${child.id}`} className="chip">
              {child.name}
            </Link>
          </li>
        ))}
      </ul>
    </section>
  );
}

/**
 * Picks another group to report into. A search box rather than a full
 * dropdown for the same reason `AddMemberPicker` is: a church with a hundred
 * cells cannot be a `<select>`.
 *
 * A cycle (choosing a group that is actually one of this one's own
 * descendants) is refused by the server, not filtered out of these results —
 * walking the whole tree client-side to pre-filter it would cost more than
 * just surfacing the one error a caller could actually hit.
 */
function ParentGroupPicker({
  excludeId,
  onPick,
}: {
  excludeId: string;
  onPick: (parentGroupId: string) => void;
}) {
  const { t } = useI18n();
  const [search, setSearch] = useState('');

  const query = useQuery({
    queryKey: ['groups', 'picker', search],
    queryFn: ({ signal }) => groupsApi.list({ search, limit: 8 }, signal),
    enabled: search.trim().length >= 2,
  });

  const candidates = (query.data?.data ?? []).filter(
    (candidate: GroupSummaryResponse) => candidate.id !== excludeId && !candidate.archivedAt,
  );

  return (
    <div className="member-picker">
      <Field
        label={t('groups.group.chooseParent')}
        name="parent-search"
        type="search"
        value={search}
        onChange={(event) => setSearch(event.target.value)}
        placeholder={t('groups.group.chooseParent')}
      />

      {candidates.length > 0 && (
        <ul className="picker-results">
          {candidates.map((candidate) => (
            <li key={candidate.id}>
              <Button
                variant="ghost"
                onClick={() => {
                  onPick(candidate.id);
                  setSearch('');
                }}
              >
                {candidate.name}
              </Button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function ArchiveButton({
  group,
  onConfirm,
}: {
  group: GroupDetailResponse;
  onConfirm: (reason: string) => void;
}) {
  const { t } = useI18n();
  const [isOpen, setIsOpen] = useState(false);
  const [reason, setReason] = useState('');

  if (!isOpen) {
    return (
      <Button variant="danger" onClick={() => setIsOpen(true)}>
        {t('common.actions.archive')}
      </Button>
    );
  }

  return (
    <form
      className="inline-confirm"
      onSubmit={(event: FormEvent) => {
        event.preventDefault();
        onConfirm(reason.trim());
      }}
    >
      <p>{t('groups.group.archiveConfirm', { values: { name: group.name } })}</p>
      <Field
        label={t('groups.group.archiveReason')}
        name="reason"
        value={reason}
        onChange={(event) => setReason(event.target.value)}
        required
        autoFocus
      />
      <Button type="button" variant="ghost" onClick={() => setIsOpen(false)}>
        {t('common.actions.cancel')}
      </Button>
      <Button type="submit" variant="danger" disabled={reason.trim().length === 0}>
        {t('common.actions.confirm')}
      </Button>
    </form>
  );
}

function describeError(error: unknown, t: ReturnType<typeof useI18n>['t']): string {
  return error instanceof ApiError
    ? t(`errors.${error.code}`, { fallback: t('errors.INTERNAL_ERROR') })
    : t('errors.INTERNAL_ERROR');
}
